import '@/styles/daily-tasks-layout.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { assetUrl, resolveMediaUrl } from '@/lib/assetUrl';
import {
  useClaimDailyTask,
  useClaimDailyTaskFinal,
  useDailyTasks,
  useDailyTasksProgressPoll,
  type DailyTaskFinalReward,
  type DailyTaskWithProgress,
  type DailyTasksState,
} from '@/hooks/useDailyTasks';

const DEFAULT_TASK_ICON = '/assets/icons/menu/daily.webp';

declare global {
  interface Window {
    bootstrap?: {
      Tooltip: new (
        el: Element,
        opts?: { placement?: string; trigger?: string },
      ) => { dispose: () => void };
    };
  }
}

type RewardState = 'claimable' | 'claimed' | 'incomplete';

function getRewardState(completed: boolean, claimed: boolean): RewardState {
  if (claimed) return 'claimed';
  if (completed) return 'claimable';
  return 'incomplete';
}

function TaskRewardIcon({
  taskId,
  rewardImageUrl,
  rewardName,
  rewardQuantity,
  rewardState,
  onClaim,
  claiming,
}: {
  taskId: string;
  rewardImageUrl: string;
  rewardName: string;
  rewardQuantity: number;
  rewardState: RewardState;
  onClaim: (taskId: string) => void;
  claiming: boolean;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const tooltipText = `${rewardName} x${rewardQuantity}`;

  useEffect(() => {
    const el = imgRef.current;
    if (!el || !window.bootstrap?.Tooltip) return;

    const tooltip = new window.bootstrap.Tooltip(el, {
      placement: 'top',
      trigger: 'hover',
    });

    return () => tooltip.dispose();
  }, [tooltipText]);

  const isClaimable = rewardState === 'claimable';

  return (
    <div
      className={`dailytasks-item-reward-icon dailytasks-reward-img-${rewardState}${
        claiming ? ' dailytasks-reward-icon-pending' : ''
      }`}
    >
      <button
        type="button"
        className={`dailytasks-reward-btn${isClaimable ? ' dailytasks-reward-btn-claimable' : ''}`}
        disabled={!isClaimable || claiming}
        onClick={() => {
          if (isClaimable) onClaim(taskId);
        }}
      aria-label={tooltipText}
    >
      <img
        ref={imgRef}
        src={rewardImageUrl}
        alt={rewardName}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={tooltipText}
      />
      </button>
      <div className="dailytasks-item-reward-badge">x{rewardQuantity}</div>
      {rewardState === 'claimed' ? (
        <div className="dailytasks-claimed-overlay">
          <i className="ph-light ph-check-circle" />
        </div>
      ) : null}
    </div>
  );
}

function DailyTaskItem({
  item,
  claimingTaskId,
  onClaim,
}: {
  item: DailyTaskWithProgress;
  claimingTaskId: string | null;
  onClaim: (taskId: string) => void;
}) {
  const { t } = useLocale();
  const { task, progress } = item;
  const completed = progress.completed === 1;
  const claimed = progress.claimed === 1;
  const currentProgress = progress.current_progress ?? 0;
  const required = task.required_quantity;
  const rewardState = getRewardState(completed, claimed);
  const iconUrl = task.icon_url ? resolveMediaUrl(task.icon_url) : assetUrl(DEFAULT_TASK_ICON);
  const rewardImageUrl = task.reward_image_url ? resolveMediaUrl(task.reward_image_url) : '';
  const rewardName = task.SN_Name ?? task.reward_codename128;

  return (
    <div
      className={`dailytasks-item${completed ? ' completed' : ''}${claimed ? ' claimed' : ''}`}
      data-task-id={task.task_id}
    >
      <div className="dailytasks-item-content">
        <div className="dailytasks-item-top">
          <div className="dailytasks-item-task-icon">
            <img src={iconUrl} alt={task.name} />
          </div>
          <div className="dailytasks-item-info">
            <h4 className="dailytasks-item-name">{task.name}</h4>
            {task.description ? (
              <p className="dailytasks-item-description">{task.description}</p>
            ) : null}
          </div>
          <div className="dailytasks-item-reward-icon-wrapper">
            {rewardImageUrl ? (
              <TaskRewardIcon
                taskId={task.task_id}
                rewardImageUrl={rewardImageUrl}
                rewardName={rewardName}
                rewardQuantity={task.reward_quantity}
                rewardState={rewardState}
                onClaim={onClaim}
                claiming={claimingTaskId === task.task_id}
              />
            ) : null}
          </div>
        </div>

        <div className="dailytasks-item-bottom">
          <div className="dailytasks-item-progress-info">
            <span className="dailytasks-status-text">
              {claimed ? t('dt_claimed') : completed ? t('job_completed') : t('dt_incomplete')}
            </span>
            <span className="dailytasks-progress-numbers">
              {currentProgress} / {required}
            </span>
          </div>
          <div className="dailytasks-item-progress-bar-container">
            <progress
              aria-label={task.name}
              className="dailytasks-item-progress-bar"
              value={currentProgress}
              max={required > 0 ? required : 1}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FinalRewardRow({
  reward,
  isClaimed,
  canClaim,
  claiming,
  onClaim,
}: {
  reward: DailyTaskFinalReward;
  isClaimed: boolean;
  canClaim: boolean;
  claiming: boolean;
  onClaim: (id: number) => void;
}) {
  const { t } = useLocale();
  const imgSrc = reward.reward_image_url ? resolveMediaUrl(reward.reward_image_url) : '';
  const rewardName = reward.SN_Name ?? reward.reward_codename128;

  return (
    <div className={`dailytasks-final-reward-item${isClaimed ? ' claimed' : ''}`}>
      {imgSrc ? (
        <img src={imgSrc} alt={rewardName} className="dailytasks-final-reward-img" />
      ) : null}
      <div className="dailytasks-final-reward-info">
        <span className="dailytasks-final-reward-text">
          {rewardName} x{reward.reward_quantity}
        </span>
      </div>
      {isClaimed ? (
        <span className="dailytasks-final-claimed-text">{t('dt_claimed')}</span>
      ) : canClaim ? (
        <button
          type="button"
          className="dailytasks-final-claim-btn"
          data-final-reward-id={reward.id}
          disabled={claiming}
          onClick={() => onClaim(reward.id)}
        >
          {claiming ? t('dt_processing') : t('dt_claim_final_reward')}
        </button>
      ) : (
        <span className="dailytasks-final-incomplete-text">{t('dt_complete_all_first')}</span>
      )}
    </div>
  );
}

function DailyTasksContent({ data }: { data: DailyTasksState }) {
  const { t } = useLocale();
  const { flash, showFlash } = useIngameFlash();
  const claimTaskMutation = useClaimDailyTask();
  const claimFinalMutation = useClaimDailyTaskFinal();
  const [claimingTaskId, setClaimingTaskId] = useState<string | null>(null);
  const [claimingFinalId, setClaimingFinalId] = useState<number | null>(null);

  useDailyTasksProgressPoll(data.success && data.tasks.length > 0);

  const stats = data.stats;
  const canClaimFinal =
    stats.total > 0 && stats.completed === stats.total && stats.claimed === stats.total;

  const handleClaimTask = useCallback(
    async (taskId: string) => {
      setClaimingTaskId(taskId);
      try {
        const result = await claimTaskMutation.mutateAsync(taskId);
        if (result.success) {
          showFlash(`${t('dt_success')}: ${result.message ?? ''}`, 'success');
        } else {
          showFlash(result.message ?? t('dt_error'), 'danger');
        }
      } catch {
        showFlash(t('dt_network_error'), 'danger');
      } finally {
        setClaimingTaskId(null);
      }
    },
    [claimTaskMutation, showFlash, t],
  );

  const handleClaimFinal = useCallback(
    async (finalRewardId: number) => {
      setClaimingFinalId(finalRewardId);
      try {
        const result = await claimFinalMutation.mutateAsync(finalRewardId);
        if (result.success) {
          showFlash(`${t('dt_success')}: ${result.message ?? ''}`, 'success');
        } else {
          showFlash(result.message ?? t('dt_error'), 'danger');
        }
      } catch {
        showFlash(t('dt_network_error'), 'danger');
      } finally {
        setClaimingFinalId(null);
      }
    },
    [claimFinalMutation, showFlash, t],
  );

  if (!data.success && data.error) {
    return (
      <div className="dailytasks-alert dailytasks-alert-danger" role="alert">
        {data.error}
      </div>
    );
  }

  return (
    <>
      <IngameFlash flash={flash} />

      <div className="dailytasks-global-progress">
        <div className="dailytasks-progress-header">
          <h3>{t('dt_global_progress')}</h3>
          <span className="dailytasks-progress-text">
            {stats.completed} / {stats.total} {t('dt_tasks_completed')}
          </span>
        </div>
        <div className="dailytasks-progress-bar-container">
          <progress
            aria-label={t('dt_global_progress')}
            className="dailytasks-progress-bar"
            value={stats.completed}
            max={stats.total > 0 ? stats.total : 1}
          />
        </div>
      </div>

      <div className="dailytasks-list">
        {data.tasks.length === 0 ? (
          <div className="dailytasks-empty">{t('dt_no_tasks')}</div>
        ) : (
          data.tasks.map((item) => (
            <DailyTaskItem
              key={item.task.task_id}
              item={item}
              claimingTaskId={claimingTaskId}
              onClaim={(taskId) => void handleClaimTask(taskId)}
            />
          ))
        )}
      </div>

      {data.finalRewards.length > 0 ? (
        <div className="dailytasks-final-rewards">
          <h3 className="dailytasks-final-title">{t('dt_final_rewards')}</h3>
          <p className="dailytasks-final-description">{t('dt_final_rewards_description')}</p>
          <div className="dailytasks-final-rewards-list">
            {data.finalRewards.map((reward) => (
              <FinalRewardRow
                key={reward.id}
                reward={reward}
                isClaimed={data.claimedFinalRewards.includes(reward.id)}
                canClaim={canClaimFinal}
                claiming={claimingFinalId === reward.id}
                onClaim={(id) => void handleClaimFinal(id)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

export function DailyTasksPage() {
  const { t } = useLocale();
  const { data, isLoading, isError } = useDailyTasks();

  return (
    <div className="container-ingame daily-tasks-page">
      <div className="dailytasks-container">
        <SimpleMenu title={t('dt_title')} />

        <div className="dailytasks-scroll">
          {isLoading ? <div className="dailytasks-loading">...</div> : null}

          {isError ? (
            <div className="dailytasks-alert dailytasks-alert-danger" role="alert">
              {t('dt_load_error')}
            </div>
          ) : null}

          {!isLoading && !isError && data ? <DailyTasksContent data={data} /> : null}
        </div>
      </div>
    </div>
  );
}
